import {
	CodeBlock,
	Endpoint,
	Alert,
	Badge,
	Table,
	ParamList,
	Param,
	Card,
	CardGrid,
	Diagram,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Webhooks() {
	const eventTypesColumns = [
		{ key: 'event', header: 'Event Type' },
		{ key: 'description', header: 'Description' },
		{ key: 'trigger', header: 'Triggered When' },
	];

	const eventTypesData = [
		{
			event: 'bet.placed',
			description: 'A bet has been placed',
			trigger: 'Player submits a bet',
		},
		{
			event: 'bet.settled',
			description: 'A bet has been resolved',
			trigger: 'Bet outcome determined',
		},
		{
			event: 'session.created',
			description: 'New game session started',
			trigger: 'Session creation API call',
		},
		{
			event: 'session.expired',
			description: 'Session has ended',
			trigger: 'Timeout or manual termination',
		},
		{
			event: 'deposit.completed',
			description: 'Funds deposited (integrated mode)',
			trigger: 'Successful deposit',
		},
		{
			event: 'withdrawal.completed',
			description: 'Funds withdrawn (integrated mode)',
			trigger: 'Successful withdrawal',
		},
	];

	const retryScheduleColumns = [
		{ key: 'attempt', header: 'Attempt' },
		{ key: 'delay', header: 'Delay' },
		{ key: 'totalTime', header: 'Total Time' },
	];

	const retryScheduleData = [
		{ attempt: '1', delay: 'Immediate', totalTime: '0 seconds' },
		{ attempt: '2', delay: '10 seconds', totalTime: '10 seconds' },
		{ attempt: '3', delay: '30 seconds', totalTime: '40 seconds' },
		{ attempt: '4', delay: '1 minute', totalTime: '1 min 40 sec' },
		{ attempt: '5', delay: '5 minutes', totalTime: '6 min 40 sec' },
		{ attempt: '6', delay: '15 minutes', totalTime: '21 min 40 sec' },
		{ attempt: '7', delay: '30 minutes', totalTime: '51 min 40 sec' },
		{ attempt: '8', delay: '1 hour', totalTime: '1 hr 51 min' },
	];

	return (
		<div className={styles.docsPage}>
			<h1>Webhooks</h1>
			<p className={styles.lead}>
				Webhooks enable your platform to receive real-time notifications about
				events in Ringfort Studio. Use webhooks to update player statistics,
				trigger notifications, and synchronize your systems.
			</p>

			<h2>Overview</h2>
			<p>
				When events occur in Ringfort (bets placed, sessions created, etc.), we
				send HTTP POST requests to your configured webhook endpoints. Each
				request includes a signature for verification and detailed event data.
			</p>

			<Diagram
				title="Webhook Flow"
				direction="horizontal"
				steps={[
					{
						label: 'Event Occurs',
						description: 'Bet placed, session created, etc.',
						highlight: true,
					},
					{
						label: 'Ringfort Sends',
						description: 'POST with signature + payload',
					},
					{
						label: 'Your Server',
						description: 'Verify signature',
						highlight: true,
					},
					{
						label: 'Process Event',
						description: 'Update records, trigger actions',
					},
					{
						label: '200 OK',
						description: 'Confirm receipt',
					},
				]}
			/>

			<h2>Event Types</h2>
			<Table columns={eventTypesColumns} data={eventTypesData} />

			<h2>Webhook Payload Format</h2>
			<p>
				All webhook payloads follow a consistent structure with the event type,
				timestamp, and event-specific data.
			</p>

			<h3>Base Payload Structure</h3>
			<CodeBlock language="json" title="Payload Structure">
				{`{
  "id": "evt_abc123def456",
  "type": "bet.placed",
  "created": 1704067200,
  "operatorId": "op_98765",
  "data": {
    // Event-specific data
  }
}`}
			</CodeBlock>

			<ParamList>
				<Param name="id" type="string" required>
					Unique identifier for this webhook event. Use this for idempotency
					checks.
				</Param>
				<Param name="type" type="string" required>
					The event type (e.g., "bet.placed", "session.created").
				</Param>
				<Param name="created" type="number" required>
					Unix timestamp when the event occurred.
				</Param>
				<Param name="operatorId" type="string" required>
					Your operator account identifier.
				</Param>
				<Param name="data" type="object" required>
					Event-specific payload data.
				</Param>
			</ParamList>

			<h2>Event Payloads</h2>

			<h3>
				bet.placed <Badge variant="new">Common</Badge>
			</h3>
			<p>Sent when a player places a bet. This event fires immediately.</p>
			<CodeBlock language="json" title="bet.placed Payload">
				{`{
  "id": "evt_bet_placed_001",
  "type": "bet.placed",
  "created": 1704067200,
  "operatorId": "op_98765",
  "data": {
    "betId": "bet_xyz789",
    "playerId": "player_12345",
    "sessionId": "sess_abc123",
    "game": "dice",
    "amount": "1000",
    "currency": "USD",
    "betDetails": {
      "target": 50.00,
      "condition": "under",
      "multiplier": 1.98
    },
    "clientSeed": "player_seed_123",
    "serverSeedHash": "a1b2c3d4e5f6...",
    "nonce": 42,
    "timestamp": 1704067200
  }
}`}
			</CodeBlock>

			<h3>
				bet.settled <Badge variant="new">Common</Badge>
			</h3>
			<p>
				Sent when a bet is resolved with the outcome. Contains the result and
				payout information.
			</p>
			<CodeBlock language="json" title="bet.settled Payload">
				{`{
  "id": "evt_bet_settled_001",
  "type": "bet.settled",
  "created": 1704067201,
  "operatorId": "op_98765",
  "data": {
    "betId": "bet_xyz789",
    "playerId": "player_12345",
    "sessionId": "sess_abc123",
    "game": "dice",
    "amount": "1000",
    "currency": "USD",
    "result": {
      "roll": 42.35,
      "target": 50.00,
      "condition": "under"
    },
    "won": true,
    "payout": "1980",
    "multiplier": 1.98,
    "profit": "980",
    "balanceAfter": "50980",
    "provablyFair": {
      "serverSeed": "abc123def456...",
      "serverSeedHash": "a1b2c3d4e5f6...",
      "clientSeed": "player_seed_123",
      "nonce": 42,
      "verified": true
    },
    "isDemo": false,
    "timestamp": 1704067201
  }
}`}
			</CodeBlock>

			<h3>session.created</h3>
			<p>Sent when a new game session is created via the API.</p>
			<CodeBlock language="json" title="session.created Payload">
				{`{
  "id": "evt_session_created_001",
  "type": "session.created",
  "created": 1704067100,
  "operatorId": "op_98765",
  "data": {
    "sessionId": "sess_abc123",
    "playerId": "player_12345",
    "currency": "USD",
    "walletMode": "seamless",
    "game": "dice",
    "ipAddress": "192.168.1.1",
    "userAgent": "Mozilla/5.0...",
    "expiresAt": 1704081500,
    "metadata": {
      "campaign": "summer_promo",
      "vipLevel": "gold"
    },
    "isDemo": false,
    "timestamp": 1704067100
  }
}`}
			</CodeBlock>

			<h3>session.expired</h3>
			<p>
				Sent when a session ends due to timeout, manual termination, or player
				exit.
			</p>
			<CodeBlock language="json" title="session.expired Payload">
				{`{
  "id": "evt_session_expired_001",
  "type": "session.expired",
  "created": 1704081500,
  "operatorId": "op_98765",
  "data": {
    "sessionId": "sess_abc123",
    "playerId": "player_12345",
    "reason": "timeout",
    "duration": 14400,
    "stats": {
      "totalBets": 150,
      "totalWagered": "75000",
      "totalWon": "82500",
      "netResult": "7500",
      "largestBet": "5000",
      "largestWin": "9900"
    },
    "finalBalance": "57500",
    "timestamp": 1704081500
  }
}`}
			</CodeBlock>

			<Alert variant="info" title="Session Expiration Reasons">
				<ul>
					<li>
						<code>timeout</code> - Session expired due to inactivity
					</li>
					<li>
						<code>manual</code> - Terminated via API call
					</li>
					<li>
						<code>player_exit</code> - Player clicked exit button
					</li>
					<li>
						<code>new_session</code> - New session created for same player
					</li>
				</ul>
			</Alert>

			<h3>deposit.completed</h3>
			<p>Sent when a deposit is successfully processed (integrated mode only).</p>
			<CodeBlock language="json" title="deposit.completed Payload">
				{`{
  "id": "evt_deposit_001",
  "type": "deposit.completed",
  "created": 1704067000,
  "operatorId": "op_98765",
  "data": {
    "transactionId": "tx_dep_abc123",
    "playerId": "player_12345",
    "amount": "50000",
    "currency": "USD",
    "balanceBefore": "10000",
    "balanceAfter": "60000",
    "reference": "your_ref_123",
    "timestamp": 1704067000
  }
}`}
			</CodeBlock>

			<h3>withdrawal.completed</h3>
			<p>
				Sent when a withdrawal is successfully processed (integrated mode only).
			</p>
			<CodeBlock language="json" title="withdrawal.completed Payload">
				{`{
  "id": "evt_withdrawal_001",
  "type": "withdrawal.completed",
  "created": 1704068000,
  "operatorId": "op_98765",
  "data": {
    "transactionId": "tx_wth_def456",
    "playerId": "player_12345",
    "amount": "25000",
    "currency": "USD",
    "balanceBefore": "60000",
    "balanceAfter": "35000",
    "reference": "your_ref_456",
    "timestamp": 1704068000
  }
}`}
			</CodeBlock>

			<h2>Signature Verification</h2>
			<p>
				Every webhook request includes a signature in the <code>X-Signature</code>{' '}
				header. You must verify this signature to ensure the request came from
				Ringfort.
			</p>

			<h3>Signature Algorithm</h3>
			<p>
				The signature is computed using HMAC-SHA256 with your secret API key over
				the timestamp and payload.
			</p>
			<CodeBlock language="bash" title="Signature Format">
				{`signature = HMAC-SHA256(secret_key, timestamp + "." + payload)

Headers:
X-Signature: 8a7b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b
X-Timestamp: 1704067200`}
			</CodeBlock>

			<h3>Complete Verification Example</h3>
			<CodeBlock language="typescript" title="Webhook Handler with Verification">
				{`import express from 'express';
import crypto from 'crypto';

const app = express();
const WEBHOOK_SECRET = process.env.RINGFORT_SECRET_KEY;

// Verify webhook signature
function verifyWebhookSignature(
  payload: string,
  timestamp: string,
  signature: string
): boolean {
  // Check timestamp is within 5 minutes
  const now = Math.floor(Date.now() / 1000);
  const requestTime = parseInt(timestamp, 10);

  if (Math.abs(now - requestTime) > 300) {
    console.error('Webhook timestamp too old or in future');
    return false;
  }

  // Compute expected signature
  const message = \`\${timestamp}.\${payload}\`;
  const expected = crypto
    .createHmac('sha256', WEBHOOK_SECRET)
    .update(message)
    .digest('hex');

  // Use timing-safe comparison to prevent timing attacks
  try {
    return crypto.timingSafeEqual(
      Buffer.from(signature, 'hex'),
      Buffer.from(expected, 'hex')
    );
  } catch {
    return false;
  }
}

// Webhook endpoint - use raw body for signature verification
app.post(
  '/webhooks/ringfort',
  express.raw({ type: 'application/json' }),
  async (req, res) => {
    const signature = req.headers['x-signature'] as string;
    const timestamp = req.headers['x-timestamp'] as string;
    const payload = req.body.toString();

    // Verify signature
    if (!signature || !timestamp) {
      console.error('Missing signature headers');
      return res.status(401).json({ error: 'Missing signature headers' });
    }

    if (!verifyWebhookSignature(payload, timestamp, signature)) {
      console.error('Invalid webhook signature');
      return res.status(401).json({ error: 'Invalid signature' });
    }

    // Parse the verified payload
    const event = JSON.parse(payload);

    // Check for duplicate events (idempotency)
    const alreadyProcessed = await checkEventProcessed(event.id);
    if (alreadyProcessed) {
      console.log('Duplicate webhook, already processed:', event.id);
      return res.json({ received: true, duplicate: true });
    }

    // Process the event
    try {
      await processWebhookEvent(event);

      // Mark as processed
      await markEventProcessed(event.id);

      res.json({ received: true });
    } catch (error) {
      console.error('Error processing webhook:', error);
      // Return 500 to trigger retry
      res.status(500).json({ error: 'Processing failed' });
    }
  }
);

// Event processing logic
async function processWebhookEvent(event: WebhookEvent) {
  switch (event.type) {
    case 'bet.placed':
      await handleBetPlaced(event.data);
      break;

    case 'bet.settled':
      await handleBetSettled(event.data);
      break;

    case 'session.created':
      await handleSessionCreated(event.data);
      break;

    case 'session.expired':
      await handleSessionExpired(event.data);
      break;

    case 'deposit.completed':
      await handleDepositCompleted(event.data);
      break;

    case 'withdrawal.completed':
      await handleWithdrawalCompleted(event.data);
      break;

    default:
      console.log('Unknown event type:', event.type);
  }
}

// Example event handlers
async function handleBetPlaced(data: BetPlacedData) {
  // Update real-time dashboard
  await updateDashboard({
    playerId: data.playerId,
    game: data.game,
    amount: data.amount,
    action: 'bet_placed',
  });

  // Track for analytics
  await analytics.track('bet_placed', {
    player: data.playerId,
    game: data.game,
    amount: parseInt(data.amount, 10) / 100,
    currency: data.currency,
  });
}

async function handleBetSettled(data: BetSettledData) {
  // Update player statistics
  await updatePlayerStats(data.playerId, {
    totalBets: 1,
    totalWagered: parseInt(data.amount, 10),
    totalWon: data.won ? parseInt(data.payout, 10) : 0,
  });

  // Check for big wins
  const profit = parseInt(data.profit, 10);
  if (data.won && profit > 100000) {
    // $1000+ win
    await notifyBigWin(data.playerId, data.game, profit);
  }

  // Update leaderboards
  if (data.won) {
    await updateLeaderboard(data.playerId, data.game, profit);
  }
}

async function handleSessionExpired(data: SessionExpiredData) {
  // Log session summary
  console.log(\`Session \${data.sessionId} ended:\`, {
    player: data.playerId,
    duration: \`\${Math.floor(data.duration / 60)} minutes\`,
    bets: data.stats.totalBets,
    netResult: data.stats.netResult,
  });

  // Update player last activity
  await updatePlayerLastActivity(data.playerId);
}

interface WebhookEvent {
  id: string;
  type: string;
  created: number;
  operatorId: string;
  data: unknown;
}

interface BetPlacedData {
  betId: string;
  playerId: string;
  sessionId: string;
  game: string;
  amount: string;
  currency: string;
}

interface BetSettledData {
  betId: string;
  playerId: string;
  won: boolean;
  amount: string;
  payout: string;
  profit: string;
  game: string;
}

interface SessionExpiredData {
  sessionId: string;
  playerId: string;
  duration: number;
  stats: {
    totalBets: number;
    netResult: string;
  };
}`}
			</CodeBlock>

			<h2>Retry Logic and Error Handling</h2>
			<p>
				If your webhook endpoint returns a non-2xx status code or times out,
				Ringfort will retry the delivery with exponential backoff.
			</p>

			<h3>Retry Schedule</h3>
			<Table columns={retryScheduleColumns} data={retryScheduleData} />

			<Alert variant="warning" title="Webhook Timeout">
				Your webhook endpoint must respond within <strong>30 seconds</strong>. If
				processing takes longer, acknowledge the webhook immediately and process
				asynchronously.
			</Alert>

			<h3>Async Processing Pattern</h3>
			<CodeBlock language="typescript" title="Async Webhook Processing">
				{`import { Queue } from 'bullmq';

const webhookQueue = new Queue('webhooks');

app.post('/webhooks/ringfort', async (req, res) => {
  // Verify signature first
  if (!verifySignature(req)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const event = JSON.parse(req.body.toString());

  // Immediately acknowledge receipt
  res.json({ received: true });

  // Queue for async processing
  await webhookQueue.add('process-webhook', {
    event,
    receivedAt: Date.now(),
  });
});

// Worker processes webhooks asynchronously
const worker = new Worker('webhooks', async (job) => {
  const { event } = job.data;

  // Check idempotency
  if (await checkEventProcessed(event.id)) {
    return { skipped: true, reason: 'duplicate' };
  }

  // Process the event (can take as long as needed)
  await processWebhookEvent(event);

  // Mark as processed
  await markEventProcessed(event.id);

  return { processed: true };
});`}
			</CodeBlock>

			<h3>Handling Failed Webhooks</h3>
			<p>
				If all retry attempts fail, the webhook is marked as failed. You can view
				failed webhooks in the operator dashboard and manually retry them.
			</p>
			<Endpoint
				method="GET"
				path="/api/v1/operators/webhooks/failed"
				description="List failed webhook deliveries"
			/>
			<Endpoint
				method="POST"
				path="/api/v1/operators/webhooks/:webhookId/retry"
				description="Manually retry a failed webhook"
			/>

			<h2>Configuring Webhooks</h2>
			<p>
				Configure your webhook endpoints in the Ringfort operator dashboard or
				via API.
			</p>

			<h3>Dashboard Configuration</h3>
			<ol>
				<li>Log in to the Ringfort operator dashboard</li>
				<li>Navigate to Settings &gt; Webhooks</li>
				<li>Enter your webhook URL (must be HTTPS in production)</li>
				<li>Select which events to subscribe to</li>
				<li>Save and test with the "Send Test Event" button</li>
			</ol>

			<h3>API Configuration</h3>
			<Endpoint
				method="PUT"
				path="/api/v1/operators/settings/webhooks"
				description="Configure webhook endpoints"
			/>
			<CodeBlock language="typescript" title="Configure Webhooks via API">
				{`const configureWebhooks = async () => {
  const response = await fetch(
    'https://api.rngfort.com/api/v1/operators/settings/webhooks',
    {
      method: 'PUT',
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: 'https://yoursite.com/webhooks/ringfort',
        events: [
          'bet.placed',
          'bet.settled',
          'session.created',
          'session.expired',
        ],
        enabled: true,
      }),
    }
  );

  return response.json();
};`}
			</CodeBlock>

			<h2>Testing Webhooks</h2>
			<p>Use the test endpoint to verify your webhook handler is working correctly.</p>
			<Endpoint
				method="POST"
				path="/api/v1/operators/webhooks/test"
				description="Send a test webhook event"
			/>
			<CodeBlock language="typescript" title="Send Test Webhook">
				{`const sendTestWebhook = async (eventType: string) => {
  const response = await fetch(
    'https://api.rngfort.com/api/v1/operators/webhooks/test',
    {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer sk_test_xxxxxxxxxxxxx',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: eventType, // e.g., 'bet.settled'
      }),
    }
  );

  const result = await response.json();

  if (result.success) {
    console.log('Test webhook sent successfully');
    console.log('Delivery status:', result.data.deliveryStatus);
    console.log('Response time:', result.data.responseTimeMs, 'ms');
  } else {
    console.error('Test webhook failed:', result.error);
  }
};

// Test all event types
await sendTestWebhook('bet.placed');
await sendTestWebhook('bet.settled');
await sendTestWebhook('session.created');
await sendTestWebhook('session.expired');`}
			</CodeBlock>

			<h2>Best Practices</h2>

			<h3>Security</h3>
			<ul>
				<li>
					<strong>Always verify signatures:</strong> Never process webhooks
					without verifying the HMAC signature
				</li>
				<li>
					<strong>Use HTTPS:</strong> Your webhook endpoint must use HTTPS in
					production
				</li>
				<li>
					<strong>Validate timestamps:</strong> Reject webhooks with timestamps
					older than 5 minutes
				</li>
				<li>
					<strong>Implement IP allowlisting:</strong> Optionally restrict to
					Ringfort IP ranges
				</li>
			</ul>

			<h3>Reliability</h3>
			<ul>
				<li>
					<strong>Implement idempotency:</strong> Track processed event IDs to
					handle duplicate deliveries
				</li>
				<li>
					<strong>Respond quickly:</strong> Return 200 within 30 seconds; process
					asynchronously if needed
				</li>
				<li>
					<strong>Handle errors gracefully:</strong> Return 5xx for temporary
					failures to trigger retries
				</li>
				<li>
					<strong>Monitor delivery:</strong> Set up alerts for failed webhook
					deliveries
				</li>
			</ul>

			<h3>Development</h3>
			<ul>
				<li>
					<strong>Use ngrok for local testing:</strong> Expose your local server
					for webhook testing
				</li>
				<li>
					<strong>Log all webhooks:</strong> Store raw webhook data for debugging
				</li>
				<li>
					<strong>Test all event types:</strong> Ensure handlers work for every
					subscribed event
				</li>
			</ul>

			<CodeBlock language="bash" title="Local Testing with ngrok">
				{`# Start your local server
npm run dev

# In another terminal, expose with ngrok
ngrok http 3000

# Use the ngrok URL in Ringfort dashboard
# https://abc123.ngrok.io/webhooks/ringfort`}
			</CodeBlock>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<CardGrid columns={3}>
					<Card
						title="API Reference"
						description="Complete API documentation"
						href="/docs/api-operators"
					/>
					<Card
						title="Error Codes"
						description="Handle webhook errors"
						href="/docs/errors"
					/>
					<Card
						title="Testing Guide"
						description="Test your integration"
						href="/docs/testing-guide"
					/>
				</CardGrid>
			</div>
		</div>
	);
}
